// Import internal
import { Common } from './common.model';
import { Embedded, EmbeddedUser, toUserEmbedded, toUsersEmbedded } from './embed.model';
import { Link } from './link.model';

export type embed = keyof EmbedProperties;
export type AnalyticsCodes = Array<{ name: string; count: number}>;

export interface EmbedProperties {
    $createdBy: EmbeddedUser;
    $updatedBy: EmbeddedUser;
    $deletedBy: EmbeddedUser;
    $leaders: EmbeddedUser[];
}

export type EmbedTribe<T extends embed> = Tribe & Partial<Pick<EmbedProperties, T>>;

export const configuration: {[K in embed]: {embed: string; transform: (project: Tribe, embedded: Embedded) => EmbedProperties[K] }} = {
    $createdBy: {
        embed: 'users',
        transform: (data: Tribe, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'createdBy', embedded),
    },
    $updatedBy: {
        embed: 'users',
        transform: (data: Tribe, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'updatedBy', embedded),
    },
    $deletedBy: {
        embed: 'users',
        transform: (data: Tribe, embedded: Embedded): EmbeddedUser => toUserEmbedded(data, 'deletedBy', embedded),
    },
    $leaders: {
        embed: 'users',
        transform: (data: Tribe, embedded: Embedded): EmbeddedUser[] => toUsersEmbedded(data, 'leaders', embedded),
    },
};

export class Tribe extends Common {
    public name: string;
    public type: TribeTypes;
    public analyticsCode: string;
    public leaders: string[];
    public avatar: string;
    public color: string;
    public links: Link[];

    constructor(data: any) {
        super(data);
        this.id = data.id;
        this.name = data.name;
        this.color = data.color;
        this.analyticsCode = data.analyticsCode;
        this.type = data.type;
        this.leaders = data.leaders ?? [];
        this.avatar = data.avatar;
        this.links = data.links ?? [];
    }

    public static toCustomTribe<T extends embed>(data: Tribe, embedded: Embedded, customFields: T[]): EmbedTribe<T> {
        const customData = customFields.reduce((acc, field: embed) => {
            acc[field] = configuration[field].transform(data, embedded);
            return acc;
        }, {});
        return {...new Tribe(data), ...customData} as EmbedTribe<T>;
    }
}

export enum TribeTypes {
    BUSINESS_UNIT = 'BUSINESS_UNIT',
    PRACTICE = 'PRACTICE',
    EXPERTISE = 'EXPERTISE',
    FEDERAL = 'FEDERAL',
    TRANSVERSE_SERVICES = 'TRANSVERSE_SERVICES',
}
